/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MDBox from "components/MDBox";
import { encodeInvId } from "utils/invoiceCrypto";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "utils/toast";
import { useMaterialUIController } from "context";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import Papa from "papaparse";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatCreatedDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const month = MONTH_NAMES[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
};

const STATUS_STYLES = {
  Finalized: { bg: "#d4edda", color: "#155724" },
  Draft:     { bg: "#fff3cd", color: "#856404" },
};

const filterFieldSx = { minWidth: "220px", "& .MuiOutlinedInput-root": { height: "38px" } };
const filterSelectSx = {
  minWidth: "220px",
  "& .MuiOutlinedInput-root": { height: "38px" },
  "& .MuiSelect-select": { fontSize: "0.8rem" },
};
const filterLabelProps = { shrink: true, style: { fontSize: "0.8rem" } };
const filterInputProps = { style: { fontSize: "0.8rem" } };

function StatusBadge({ value }) {
  const style = STATUS_STYLES[value] ?? { bg: "#e2e3e5", color: "#383d41" };
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </MDBox>
  );
}

function Invoices() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const api = useApi();
  const navigate = useNavigate();

  const { predefinedRanges } = reportsLineChartData;
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [dateRange, setDateRange] = useState([firstOfMonth, today]);

  const filteredRows = rows.filter((row) => {
    const status = row.isDraft ? "Draft" : "Finalized";
    const matchesStatus = statusFilter === "All" || status === statusFilter;

    const term = search.trim().toLowerCase();
    const matchesSearch =
      !term ||
      [row.invoiceNumber, row.billingCompany]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));

    return matchesStatus && matchesSearch;
  });

  const columns = [
    { Header: "Invoice No", accessor: "invoiceNumber", width: "14%", align: "left" },
    {
      Header: "Created Date",
      accessor: "createdDate",
      width: "12%",
      align: "left",
      Cell: ({ value }) => formatCreatedDate(value),
    },
    { Header: "Billing To", accessor: "billingCompany", width: "12%", align: "left" },
    { Header: "Amount", accessor: (row) => `${row.totalAmount} ${row.currency}`, width: "10%", align: "left" },
    {
      Header: "Status",
      accessor: (row) => (row.isDraft ? "Draft" : "Finalized"),
      width: "9%",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => <StatusBadge value={value} />,
    },
    {
      Header: "Invoice Link",
      id: "invoiceLink",
      accessor: "invoiceNumber",
      width: "9%",
      align: "center",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        const portalBase = (process.env.REACT_APP_PORTAL_BASE_URL || "").replace(/\/$/, "");
        const url = `${portalBase}/invoices/view/${encodeInvId(row.original.id)}`;
        return (
          <IconButton
            size="small"
            title={url}
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(url);
              toast.success("Invoice link copied!");
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        );
      },
    },
    {
      Header: "Payment Link",
      id: "paymentLink",
      accessor: "paymentLink",
      width: "9%",
      align: "center",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        // eslint-disable-next-line react/prop-types
        const { hasPaymentLink, paymentLink } = row.original;
        if (hasPaymentLink !== "1" || !paymentLink) {
          return <MDTypography variant="caption" color="text">—</MDTypography>;
        }
        return (
          <IconButton
            size="small"
            title={paymentLink}
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(paymentLink);
              toast.success("Payment link copied!");
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  const fetchData = (range) => {
    if (!range || range.length !== 2) return;
    const fmt = (date) => new Date(date).toISOString().split("T")[0];
    const params = { fromDate: fmt(range[0]), toDate: fmt(range[1]) };
    api
      .get("invoices", { params })
      .then((response) => {
        const invoices = response.data?.invoices ?? [];
        setRows(invoices);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setRows([]);
      });
  };

  const handleDownloadCSV = () => {
    if (rows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const exportCols = columns.filter((c) => c.Header !== "Invoice Link");
    const headers = exportCols.map((c) => c.Header);
    const values = rows.map((row) =>
      exportCols.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    const csv = Papa.unparse([headers, ...values], { quotes: true });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Invoices_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    fetchData(dateRange);
  }, []); // eslint-disable-line

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox mb={3} display="flex" alignItems="center">
              <MDBox flex={1}>
                <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.25 }}>
                  Invoices
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular" sx={{ lineHeight: 1 }}>
                  {rows.length} invoice{rows.length === 1 ? "" : "s"}
                </MDTypography>
              </MDBox>
              <DateRangePicker
                disabledDate={afterToday()}
                ranges={predefinedRanges}
                onChange={(range) => {
                  if (range && range.length === 2) {
                    setDateRange(range);
                    fetchData(range);
                  }
                }}
                format="dd MMMM yyyy"
                value={[new Date(dateRange[0]), new Date(dateRange[1])]}
              />
              <MDBox flex={1} display="flex" justifyContent="flex-end" gap={1}>
                <MDButton variant="outlined" color="secondary" size="small" onClick={handleDownloadCSV}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">download</Icon>
                  Export
                </MDButton>
                <MDButton
                  variant="gradient"
                  color={sidenavColor}
                  size="small"
                  onClick={() => navigate("/invoices/new")}
                >
                  <Icon sx={{ mr: 0.5 }} fontSize="small">add</Icon>
                  New invoice
                </MDButton>
              </MDBox>
            </MDBox>

            <Card
              sx={({ boxShadows }) => ({
                border: "1px solid",
                borderColor: "grey.300",
                boxShadow: boxShadows.sm,
                borderRadius: "12px",
                overflow: "hidden",
              })}
            >
              <MDBox
                display="flex"
                alignItems="center"
                gap={2}
                px={2.25}
                py={1.25}
                bgColor="grey-100"
                sx={{ borderBottom: "1px solid", borderColor: "grey.300" }}
              >
                <TextField
                  label="Search"
                  placeholder="Invoice no or billing name"
                  size="small"
                  variant="outlined"
                  InputLabelProps={filterLabelProps}
                  InputProps={filterInputProps}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={filterFieldSx}
                />
                <TextField
                  select
                  label="Status"
                  size="small"
                  variant="outlined"
                  InputLabelProps={filterLabelProps}
                  InputProps={filterInputProps}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={filterSelectSx}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Finalized">Finalized</MenuItem>
                  <MenuItem value="Draft">Draft</MenuItem>
                </TextField>
              </MDBox>
              <DataTable
                table={{ columns, rows: filteredRows }}
                showTotalEntries
                isSorted
                noEndBorder
                hideExpandColumn
                entriesPerPage
                entriesPerPagePosition="bottom"
                disableRowDialog
                onRowClicked={(row) => navigate(`/invoices/edit/${row.original.id}`, { state: { invoice: row.original } })}
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Invoices;
